import { useState } from 'react'
import { useReports } from '../../api/queries/reports'
import { userFacingMessage } from '../../api/errors'
import { Button } from '../../ui/Button'
import { EmptyState, ErrorState, Skeleton } from '../../ui/Feedback'
import { shellStyles } from '../../app/AppShell'
import { ReportSection } from './ReportSection'
import styles from './Reports.module.css'

/**
 * A morning briefing, not an analytics dashboard.
 *
 * The page reads in the order the backend returns its sections, and that order
 * is the argument: how the pipeline is performing, then what needs acting on
 * today, then where qualification is weak. Performance leads because the page
 * should say how things are going before it says what is wrong (Mike,
 * 2026-08-22) — nothing negative is hidden by that, it just comes second.
 *
 * One metric is open at a time across the whole page, so that state lives here
 * rather than inside a section.
 */
export function ReportsPage() {
  const reports = useReports()
  const [openKey, setOpenKey] = useState<string | null>(null)

  return (
    <>
      <header className={shellStyles.topbar}>
        <h1 className={styles.pageTitle}>Reports</h1>
      </header>

      <main className={shellStyles.content}>
        {reports.isPending ? (
          <div className={styles.loading}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={220} />
            ))}
          </div>
        ) : reports.isError ? (
          <ErrorState
            title="Could not load your reports"
            body={userFacingMessage(reports.error)}
            action={<Button onClick={() => void reports.refetch()}>Try again</Button>}
          />
        ) : !reports.data.available ? (
          <EmptyState
            title="Reports become available after 20 analysed calls"
            body={`Your account has ${reports.data.analyses_completed} so far. Every figure here counts confirmed evidence, and below that there is not enough of it for a number to mean anything.`}
          />
        ) : (
          <div className={styles.page}>
            <p className={styles.lead}>
              <span className={styles.leadCount}>
                {reports.data.active_deal_count}{' '}
                {reports.data.active_deal_count === 1 ? 'active opportunity' : 'active opportunities'}
              </span>
              . Every figure counts evidence confirmed in your analysed calls.
              <span className={styles.leadHint}>Click any card to see the opportunities it refers to.</span>
            </p>

            {reports.data.sections.map((section) => (
              <ReportSection
                key={section.key}
                section={section}
                openKey={openKey}
                onToggle={(key) => setOpenKey(openKey === key ? null : key)}
              />
            ))}

            <p className={styles.footnote}>
              <Bulb />
              Every figure is based on evidence found in your analysed calls.
            </p>
          </div>
        )}
      </main>
    </>
  )
}

const Bulb = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 18h6M10 22h4" />
    <path d="M12 2a6 6 0 0 0-3.5 10.9c.5.4.8 1 .8 1.6V15h5.4v-.5c0-.6.3-1.2.8-1.6A6 6 0 0 0 12 2Z" />
  </svg>
)
